import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { colors } from 'constants/theme'
import { useTranslation } from 'react-i18next'
import type { VisitFlagsResult } from 'utils/visitFlags.utils'

interface VisitSummaryBadgeProps {
  flags: VisitFlagsResult | null
  branchCode: string
  onEdit?: () => void
}

interface ChipStyle {
  fg: string
  bg: string
  borderColor?: string
  showDot?: boolean
}

const chipFilled = (color: string): ChipStyle => ({
  fg: color,
  bg: `${color}1F`,
  borderColor: `${color}33`,
  showDot: true
})

const chipOutlined = (color: string): ChipStyle => ({
  fg: color,
  bg: 'transparent',
  borderColor: `${color}66`,
  showDot: false
})

const renderChip = (label: string, style: ChipStyle, key?: string) => (
  <Box
    key={key}
    role="status"
    aria-label={label}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      height: '26px',
      padding: '0 10px',
      borderRadius: '13px',
      backgroundColor: style.bg,
      color: style.fg,
      border: style.borderColor ? `1px solid ${style.borderColor}` : 'none',
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: 1,
      whiteSpace: 'nowrap'
    }}
  >
    {style.showDot && (
      <Box
        component="span"
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: style.fg,
          flexShrink: 0
        }}
      />
    )}
    {label}
  </Box>
)

export default function VisitSummaryBadge(props: VisitSummaryBadgeProps) {
  const { t } = useTranslation()
  const { flags, branchCode, onEdit } = props

  // ยังไม่เลือก / กำลังโหลด → chip "ยังไม่ได้เลือก"
  if (!flags) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
        {renderChip(t('REGISTER.VISIT_BADGE.NONE'), chipOutlined(colors.disabledGray), 'none')}
      </Box>
    )
  }

  const {
    headerStatus,
    hadPriorHistory,
    visitCountOrg,
    visitCountCurrentBranch,
    oldestExistingBranch
  } = flags

  // ===== Left chip = สถานะที่สาขาปัจจุบันแบบกระชับ =====
  let primaryLabel = ''
  let primaryStyle: ChipStyle = chipFilled(colors.themeSecondColor)

  switch (headerStatus) {
    case 'BRAND_NEW':
    case 'NEW_AT_BRANCH':
      primaryLabel = t('REGISTER.VISIT_BADGE.ORG_NEW')
      primaryStyle = { ...chipFilled(colors.orange), showDot: false }
      break
    case 'EXISTING_AT_BRANCH':
      primaryLabel = t('REGISTER.VISIT_BADGE.ORG_EXISTING')
      primaryStyle = chipFilled(colors.themeSecondColor)
      break
    case 'LAPSED':
      primaryLabel = t('REGISTER.VISIT_BADGE.LAPSED')
      primaryStyle = chipFilled(colors.statusWarning || colors.orange)
      break
  }

  // ===== Right chip = "เก่าที่ X" — สาขา existing ที่เก่าที่สุด ที่ไม่ใช่สาขาปัจจุบัน =====
  const showSecondary =
    !!oldestExistingBranch &&
    !!oldestExistingBranch.branchCode &&
    oldestExistingBranch.branchCode !== branchCode

  const secondaryLabel = showSecondary
    ? t('REGISTER.VISIT_BADGE.EXISTING_AT_BRANCH', { branch: oldestExistingBranch!.branchCode })
    : null
  const secondaryStyle = chipOutlined(colors.themeSecondColor)

  // ===== Tooltip =====
  const tooltipParts: string[] = []
  switch (headerStatus) {
    case 'BRAND_NEW':
      tooltipParts.push(t('REGISTER.VISIT_BADGE.TOOLTIP_BRAND_NEW'))
      break
    case 'NEW_AT_BRANCH':
      tooltipParts.push(t('REGISTER.VISIT_BADGE.TOOLTIP_NEW_AT_BRANCH'))
      break
    case 'EXISTING_AT_BRANCH':
      tooltipParts.push(t('REGISTER.VISIT_BADGE.TOOLTIP_EXISTING_AT_BRANCH'))
      break
    case 'LAPSED':
      tooltipParts.push(t('REGISTER.VISIT_BADGE.TOOLTIP_LAPSED'))
      break
  }
  if (hadPriorHistory) tooltipParts.push(t('REGISTER.VISIT_BADGE.TOOLTIP_PRIOR_HISTORY'))
  const tooltip = tooltipParts.join(' · ')

  const showEdit = !!onEdit
  const showCount = visitCountOrg > 0

  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
        <Tooltip title={tooltip} placement="top" arrow>
          {renderChip(primaryLabel, primaryStyle, 'primary')}
        </Tooltip>
        {secondaryLabel && renderChip(secondaryLabel, secondaryStyle, 'secondary')}
        {showEdit && (
          <Tooltip title={t('REGISTER.VISIT_BADGE.EDIT_OVERRIDE') as string} placement="top">
            <IconButton
              size="small"
              onClick={onEdit}
              aria-label={t('REGISTER.VISIT_BADGE.EDIT_OVERRIDE') as string}
              sx={{ padding: '4px', color: colors.disabledGray }}
            >
              <EditOutlinedIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {showCount && (
        <Typography sx={{ fontSize: '12px', color: colors.disabledGray, lineHeight: 1, whiteSpace: 'nowrap' }}>
          {t('REGISTER.VISIT_BADGE.COUNT', {
            total: visitCountOrg,
            branch: visitCountCurrentBranch
          })}
        </Typography>
      )}
    </Box>
  )
}
